import { CardShell, EmptyState, ListSkeleton } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { useNavigate } from 'react-router-dom'
import { CampaignFilterRail } from '@/components/campaigns/CampaignFilterRail'
import { CampaignRow } from '@/components/campaigns/CampaignRow'
import { PageHeader } from '@/components/editorial/PageHeader'
import { useAuth } from '@/hooks/useAuth'
import { useCampaignListContainer } from './useCampaignListContainer'

/** The five Campaign table columns, in their fixed presentation order. */
const CAMPAIGN_COLUMNS = ['Campaign', 'Window', 'Objective', 'Geography', 'Status'] as const

/**
 * Campaign list — the list/filter container. Eyebrow "Campaigns", heading "All
 * campaigns", a count caption reflecting the currently accumulated filtered rows
 * (never a backend-wide total), and loading, error, zero-Campaigns, and
 * zero-matching-filters as four distinct renders that replace the table rather
 * than fabricating a row inside it. "New Campaign" renders only when `canCreate`
 * is true.
 */
export function CampaignsPage() {
	const navigate = useNavigate()
	const { user } = useAuth()
	const container = useCampaignListContainer(user?.role ?? '')

	return (
		<div>
			<PageHeader
				eyebrow="Campaigns"
				title="All campaigns"
				actions={
					container.capabilities.canCreate ? (
						<Button className="rounded-ds-full px-5" onPress={() => void navigate('/campaigns/new')}>
							New Campaign
						</Button>
					) : undefined
				}
			/>

			<div className="mt-6 flex flex-col gap-6 lg:flex-row">
				<div className="lg:w-72 lg:shrink-0">
					<CampaignFilterRail
						statusSelection={container.statusSelection}
						onToggleStatus={container.onToggleStatus}
						objective={container.objective}
						geography={container.geography}
						shownVariables={container.shownVariables}
						addableVariables={container.addableVariables}
						classificationSelection={container.classificationSelection}
						onToggleClassification={container.onToggleClassification}
						onAddAxis={container.onAddAxis}
						onRemoveAxis={container.onRemoveAxis}
					/>
				</div>

				<div className="min-w-0 flex-1 space-y-4">
					{container.isLoading && <ListSkeleton label="Loading Campaigns…" />}
					{container.isError && (
						<p role="alert" className="text-sm text-ds-danger">
							Unable to load Campaigns.
						</p>
					)}
					{!container.isLoading && !container.isError && (
						<>
							<p className="text-sm text-ds-text-muted">
								{container.count} Campaign{container.count === 1 ? '' : 's'}
							</p>
							{container.count === 0 && container.hasAnyCampaignsAtAll && (
								<EmptyState title="No Campaigns match the current filters." variant="filtered" />
							)}
							{container.count === 0 && !container.hasAnyCampaignsAtAll && (
								<EmptyState title="No Campaigns exist yet." />
							)}
							{container.campaigns.length > 0 && (
								// One overflow-contained wrapper, so a narrow viewport scrolls the table
								// rather than the page. The rows stay a semantic table at every width —
								// no column is hidden and no row becomes a card.
								<CardShell className="overflow-x-auto p-0">
									<table aria-label="Campaigns" className="w-full text-sm">
										<thead>
											<tr className="border-b border-ds-border">
												{CAMPAIGN_COLUMNS.map((column) => (
													<th
														key={column}
														scope="col"
														className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-ds-text-subtle"
													>
														{column}
													</th>
												))}
											</tr>
										</thead>
										<tbody>
											{container.campaigns.map((campaign) => (
												<CampaignRow
													key={campaign.campaignId}
													campaign={campaign}
													onActivate={(campaignId) => void navigate(`/campaigns/${campaignId}`)}
												/>
											))}
										</tbody>
									</table>
								</CardShell>
							)}
							{container.canLoadMore && (
								<Button variant="secondary" onPress={() => container.onLoadMore()} isDisabled={container.isLoadingMore}>
									{container.isLoadingMore ? 'Loading…' : 'Load more'}
								</Button>
							)}
						</>
					)}
				</div>
			</div>
		</div>
	)
}
